import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Game } from '@goodbuild/shared';
import { Button, Card, Eyebrow, Heading, Icon, Stripe, Text, type IconName } from '../ui/index.js';
import { api } from '../api/client.js';
import { useAccess, useGames, useTeam } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { CoverStack, type StackCover } from '../components/game/CoverStack.js';
import { TeamCard } from '../components/studio/TeamCard.js';
import { SupporterCta } from '../components/layout/SupporterCta.js';
import { openExternal } from '../auth/session.js';
import { LEGAL } from '../lib/legal.js';

export const Route = createFileRoute('/studio')({ component: Studio });

/** Only the two public fields matter here. Linking an account is on `/account`. */
interface CommunityInfo {
  discordUrl: string | null;
}

/**
 * Small counts, as words.
 *
 * "Three names" and not "3 names": this is the one heading on the page that is a sentence about
 * people rather than a figure off the sheet, and a numeral in it reads as a headcount. Past
 * six it falls back to the numeral, which is the point at which a studio has stopped counting
 * itself in words anyway.
 */
const TEAM_COUNT_WORDS: Record<number, string> = {
  1: 'One name',
  2: 'Two names',
  3: 'Three names',
  4: 'Four names',
  5: 'Five names',
  6: 'Six names',
};

/**
 * What is done under this roof.
 *
 * The disciplines panel is the part of a large studio's site that lists its departments. Ours
 * lists four, and each one is something you can go and look at: two of the people below are
 * level designers, the launcher this is being read in is ours, and so is the patcher that
 * delivered it. It is the honest version of a capability list — short, and all of it true.
 */
const DISCIPLINES: { icon: IconName; title: string; body: string }[] = [
  {
    icon: 'games',
    title: 'Design and direction',
    body: 'A game here starts as a document and an argument, and both of those happen at the studio. Nothing is briefed out and handed back finished.',
  },
  {
    icon: 'library',
    title: 'Level design',
    body: 'Two senior level designers on two titles. Blockout to final pass: the part of a game that is nobody else on the team’s job, and the part that decides whether it is any good.',
  },
  {
    icon: 'settings',
    title: 'Engineering and tools',
    body: 'Gameplay code, the tooling around it, and the launcher you are reading this in. We own the whole stack, because nobody was selling the one we needed.',
  },
  {
    icon: 'download',
    title: 'Release',
    body: 'Our own build pipeline: chunked, verified, patched in place. A build leaves the studio and lands on your machine with no storefront standing in between.',
  },
];

/** How the studio works, said plainly. Four lines, numbered, no mission statement. */
const PRINCIPLES = [
  {
    title: 'We build in the open.',
    body: 'Builds go out the day they are made, not the quarter they are finished. What you install is what we were playing at the studio this morning.',
  },
  {
    title: 'Everything, for one price.',
    body: 'There is no per-game purchase and no storefront. One supporter membership opens every game and prototype we make, including the ones that never get finished.',
  },
  {
    title: 'Players are in the room.',
    body: 'The next build gets argued about on our Discord before it exists, with the people making it in the thread.',
  },
  {
    title: 'We say so when it is not working.',
    body: 'A prototype going nowhere keeps the word prototype on it and stays on the slate. Nobody here finds out from a silence that we quietly dropped something.',
  },
];

/**
 * The three conversations a studio has with people who are not players.
 *
 * A studio this size has no careers portal, no press office and no business development desk,
 * and inventing three of them would be the first untrue thing on the page. There is one
 * mailbox and somebody reads it, so that is what it says.
 */
const DESK = [
  {
    title: 'Jobs',
    body: 'We hire when a game needs somebody we are not, and it gets said here when it happens. There is no careers portal, so send work rather than a cover letter and a person will read it.',
  },
  {
    title: 'Press and creators',
    body: 'Builds, keys and artwork on request, for the announced games and the unannounced ones. Tell us what you cover and we will sort the access out.',
  },
  {
    title: 'Publishing and platforms',
    body: 'Porting, funding, storefronts, platform holders. The same mailbox, and it is the founder who answers it.',
  },
];

/**
 * The studio screen: who we are, what we are making, and how to reach us.
 *
 * It is laid out the way a large studio lays out its own site — a title card, a sheet of
 * figures, the slate of titles, the disciplines under one roof, the people by name, the way in
 * — because that structure is what makes a studio legible from outside it, and being three
 * people is not a reason to be illegible. What the structure gets filled with is still the
 * workshop's own voice (chapter 08 of the guide): every figure on the sheet is real, every
 * title on the slate is installable today, and nowhere does it say "passionate team".
 *
 * It replaced the old community screen in the rail. The Discord account linking that screen
 * owned moved to `/account`, where the rest of somebody's membership already lives — this page
 * is the studio, not a settings panel, and the only Discord thing on it is the front door.
 */
function Studio() {
  const navigate = useNavigate();
  const { signedIn, hasAccess } = useAccess();

  const games = useGames();
  const slate = games.data ?? [];
  // Settled with nothing is not the same as still loading. It decides what the figure prints,
  // and which control on the screen takes the focus when the screen opens.
  const counted = games.isSuccess;

  // The headcount used to be `TEAM.length` on a constant in this file, which meant the figure at
  // the top and the cards below it could only ever agree because they were the same array. Both
  // read the table now, so a fourth person is a row in admin and not a release of the app.
  const members = useTeam();
  const team = members.data ?? [];

  const info = useQuery({
    queryKey: ['community'],
    queryFn: () => api.get<CommunityInfo>('/community'),
  });

  const discordUrl = info.data?.discordUrl ?? null;

  return (
    <FocusZone focusKey="studio" direction="column" className="gb-stack gb-stack--loose">
      {/* ---------------------------------------------------------- title card */}

      {/* The same drawn room the download screen opens on: a warm wash, the stripe at 45
          degrees, and a vignette that sits the type forward of both. A studio page opening on a
          heading and a paragraph is a document; this is a front door. */}
      <section className="gb-cine gb-cine--studio">
        <span className="gb-cine__wash" aria-hidden="true" />
        <span className="gb-cine__rays" aria-hidden="true" />
        <span className="gb-cine__vignette" aria-hidden="true" />

        <div className="gb-cine__copy">
          <Eyebrow tone="lemon">{'GOOD BUILD / The studio'}</Eyebrow>

          <h1 className="gb-cine__title">
            {'We make'}
            <br />
            {'games in'}
            <br />
            {'public.'}
          </h1>

          <Text render={<p />} className="gb-cine__sub">
            {
              'A games studio in Warsaw. Everything on our slate is playable today — not at a launch, not in a trailer, today — at the build we pushed this morning.'
            }
          </Text>

          <Text size="2" className="gb-cine__note">
            {'Warsaw, Poland / Windows, Steam Deck, Android / A build a day'}
          </Text>
        </div>
      </section>

      {/* --------------------------------------------------------- the figures */}

      {/* The particulars, printed the way a studio prints them. Three people is set at the size
          three hundred would be: the number is not what is being sold, the specificity is. */}
      <section className="gb-studio__facts" aria-label="The studio in figures">
        <Figure
          figure={members.isSuccess ? String(team.length).padStart(2, '0') : '—'}
          label="People"
          note="Named below, with everything each of them has worked on."
        />
        <Figure
          figure={counted ? String(slate.length).padStart(2, '0') : '—'}
          label="Titles in development"
          note="Every one of them installable right now."
        />
        <Figure figure="03" label="Platforms" note="Windows, Steam Deck and Android." />
        <Figure figure="24H" label="Desk to device" note="How long a build takes to reach you." />
      </section>

      {/* ----------------------------------------------------------- the slate */}

      <section className="gb-section">
        <Eyebrow>{'The slate'}</Eyebrow>
        <Heading size="6" className="gb-studio__lede">
          {'Everything we are making, in one stack.'}
        </Heading>
        <Text render={<p />} size="2" color="gray" style={{ maxWidth: '58ch' }}>
          {
            'No embargo, and no coming soon. Every cover here is installable this evening — press one for how finished it is, what it runs on and the build that is on it right now, or take the whole shelf.'
          }
        </Text>

        {slate.length > 0 ? (
          <div className="gb-studio__slate">
            <CoverStack covers={slate.map(slateCover)} focusPrefix="studio-slate" autoFocusFirst />

            {/* The way to the catalogue, rather than the catalogue. Games already sorts,
                filters and searches this list, and a studio page that reprinted it was a second
                worse catalogue that nobody maintained. */}
            <Focusable
              focusKey="studio-slate-all"
              onSelect={() => void navigate({ to: '/games' })}
              selectLabel="See every game"
            >
              <Button size="3" variant="surface">
                <Icon name="games" size={16} />
                {slate.length === 1 ? 'See the one game' : `See all ${String(slate.length)} games`}
              </Button>
            </Focusable>
          </div>
        ) : (
          <Card size="3" className="gb-stack gb-stack--tight">
            <Heading size="4" render={<h3 />}>
              {counted ? 'Nothing published yet.' : 'Reading the slate.'}
            </Heading>
            <Text render={<p />} size="2" color="gray">
              {counted
                ? 'The first build we publish lands here, and on the home screen, the day it is made.'
                : 'One moment.'}
            </Text>
          </Card>
        )}
      </section>

      {/* ------------------------------------------------------- the workshop */}

      <section className="gb-section">
        <Eyebrow>{'Under one roof'}</Eyebrow>
        <Heading size="6" className="gb-studio__lede">
          {'What we do here, rather than buy in.'}
        </Heading>
        <div className="gb-grid gb-grid--wide">
          {DISCIPLINES.map((discipline) => (
            <Card key={discipline.title} size="3" className="gb-pillar">
              <span className="gb-pillar__icon" aria-hidden="true">
                <Icon name={discipline.icon} size={22} />
              </span>
              <h3 className="gb-display gb-display--6">{discipline.title}</h3>
              <Text render={<p />} size="2" color="gray">
                {discipline.body}
              </Text>
            </Card>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- the people */}

      {/* The one section on this page that is about people rather than about work, so it is the
          one section that is allowed to be drawn rather than set. See `TeamCard`. */}
      <section className="gb-section">
        <Eyebrow>{'The people'}</Eyebrow>
        <Heading size="6" className="gb-studio__lede">
          {team.length > 0
            ? `${TEAM_COUNT_WORDS[team.length] ?? `${String(team.length)} names`}, and everything each of them has worked on.`
            : 'The people who make this.'}
        </Heading>
        <Text render={<p />} size="2" color="gray" style={{ maxWidth: '58ch' }}>
          {
            'The number in the corner is years in games, not a rank. Every title along the bottom of a card opens the game it names.'
          }
        </Text>

        {team.length > 0 ? (
          <FocusZone focusKey="studio-team" direction="row" className="gb-tcg-shelf">
            {team.map((member, index) => (
              <TeamCard key={member.id} member={member} index={index} total={team.length} />
            ))}
          </FocusZone>
        ) : (
          <Card size="3" className="gb-stack gb-stack--tight">
            <Heading size="4" render={<h3 />}>
              {members.isSuccess ? 'Nobody is listed yet.' : 'Reading the team.'}
            </Heading>
            <Text render={<p />} size="2" color="gray">
              {members.isSuccess
                ? 'The studio screen lists them the moment they are.'
                : 'One moment.'}
            </Text>
          </Card>
        )}
      </section>

      {/* -------------------------------------------------------- how we work */}

      <section className="gb-section">
        <Eyebrow>{'How we work'}</Eyebrow>
        <Heading size="6" className="gb-studio__lede">
          {'Four rules, and every one of them is checked in public.'}
        </Heading>
        <div className="gb-grid gb-grid--wide">
          {PRINCIPLES.map((principle, index) => (
            <Card key={principle.title} size="3" className="gb-principle">
              <span className="gb-principle__number" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="gb-stack gb-stack--tight">
                <Heading size="4" render={<h3 />}>
                  {principle.title}
                </Heading>
                <Text render={<p />} size="2" color="gray">
                  {principle.body}
                </Text>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------ come and talk */}

      <section className="gb-section">
        <Eyebrow>{'Come and talk to us'}</Eyebrow>
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4" render={<h3 />}>
            {'Everything social is on Discord.'}
          </Heading>
          <Text render={<p />} size="2" color="gray">
            {hasAccess
              ? 'General chat, announcements and every game channel. Your supporter role opens the private ones — link your Discord account on your account screen if you have not yet.'
              : 'General chat and announcements are open to anyone. The private channels, where the daily build notes and the bug threads live, are what the supporter role opens.'}
          </Text>
          <div className="gb-row">
            {discordUrl ? (
              <Focusable
                autoFocus={counted && slate.length === 0}
                focusKey="studio-discord"
                onSelect={() => void openExternal(discordUrl)}
                selectLabel="Open Discord"
              >
                <Button size="3" variant="surface">
                  <Icon name="external" size={16} />
                  {'Open Discord'}
                </Button>
              </Focusable>
            ) : null}
            {signedIn ? (
              <Focusable
                focusKey="studio-account"
                onSelect={() => void navigate({ to: '/account' })}
                selectLabel="Link your Discord"
              >
                <Button size="3" variant="ghost">
                  {'Link your Discord'}
                </Button>
              </Focusable>
            ) : null}
          </div>
        </Card>
      </section>

      {/* ----------------------------------------------------- working with us */}

      <section className="gb-section">
        <Eyebrow>{'Working with us'}</Eyebrow>
        <div className="gb-desk">
          {DESK.map((entry) => (
            <div key={entry.title} className="gb-desk__col">
              <Eyebrow small tone="lemon">
                {entry.title}
              </Eyebrow>
              <Text render={<p />} size="2" color="gray">
                {entry.body}
              </Text>
            </div>
          ))}

          {/* One address, printed once, at the size of the thing it is for. Printed rather than
              made into a button: nothing else in this app hands the screen over to a mail
              client, and a controller has no use for one that it did. */}
          <div className="gb-desk__col gb-desk__col--address">
            <Eyebrow small>{'The mailbox'}</Eyebrow>
            <p className="gb-desk__mail">{LEGAL.contactEmail}</p>
            <Text render={<p />} size="1" color="gray">
              {'All three go to the same place, and it gets read.'}
            </Text>
          </div>
        </div>

        {/* The particulars, at the foot, where a studio site carries them. It is a sole
            proprietorship and it says so: a page that dressed that up as a corporation would
            have undone every honest thing above it. */}
        <div className="gb-studio__colophon">
          <Stripe />
          <Text size="1" color="gray" mono>
            {`${LEGAL.brand} / ${LEGAL.entity} / ${LEGAL.address} / NIP ${LEGAL.nip}`}
          </Text>
        </div>
      </section>

      {hasAccess ? null : <SupporterCta gameCount={slate.length} />}
    </FocusZone>
  );
}

/** One figure off the sheet: the numeral, what it counts, and one line of context. */
function Figure({ figure, label, note }: { figure: string; label: string; note: string }) {
  return (
    <div className="gb-studio__fact">
      <span className="gb-studio__figure">{figure}</span>
      <Eyebrow small>{label}</Eyebrow>
      <Text render={<p />} size="1" color="gray">
        {note}
      </Text>
    </div>
  );
}

/**
 * One title on the slate, as the stack wants it.
 *
 * The portrait capsule first, because that is the shape a stack of covers is: this is the same
 * picture the shelf and the library draw, at the same ratio, which is the whole reason a stack
 * of them reads as a catalogue rather than as a row of thumbnails. Anything wider is the
 * fallback and `CoverStack` contains it rather than cropping it.
 */
function slateCover(game: Game): StackCover {
  return {
    id: game.id,
    slug: game.slug,
    title: game.title,
    capsuleImageUrl: game.capsuleImageUrl,
    wideImageUrl: game.coverImageUrl ?? game.smallImageUrl,
  };
}
